import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slide,
  Toolbar,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { sealingService } from '@paragon/data-connections';
import { hasSealsAndSignaturesPermission } from '@paragon/login-tools';
import { closeCurrentPopup } from '@paragon/popup-state';

import { confirmSealJob, getDefaultPortraitStampingTemplate } from '../../actions/job';
import { loadSeals } from '../../actions/seals';
import { getSortedAnnotationTemplates } from '../../selectors/annotationTemplates';
import { getMetaPdfsAndComponentsAsSortedArrays } from '../../selectors/documents';
import { RootState } from '../../store';
import TemplateDirectory from '../AnnotationTools/TemplateDirectory';
import { hasAll, toggleManyTogether } from '../MaterialDocumentTree/arrayUtilities';
import DocumentHierarchy from '../MaterialDocumentTree/DocumentHierarchy';
import MaterialDocumentTree from '../MaterialDocumentTree/MaterialDocumentTree';
import SealPreview from './SealPreview';

const buttonSx = {
  my: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.main',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  readonly selectedTemplate: number | null;
  readonly selectedPages: readonly number[];
  readonly createCoverPage: boolean;
  readonly createBackPage: boolean;
  readonly signatureUrl: string | undefined;
}

const Transition = React.forwardRef(
  (props: TransitionProps & { children: React.ReactElement<any, any> }, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
);

class SealJobSpecial extends Component<Props, State> {
  state: State = {
    selectedTemplate:
      this.props.templates.find(
        (template) => template.name === getDefaultPortraitStampingTemplate(this.props.designOrigin),
      )?.id ?? null,
    selectedPages: this.documents.getFirstPageOfEveryComponent().concat(this.documents.getFirstPageOfEveryMetaPdf()),

    createCoverPage: true,
    createBackPage: true,

    signatureUrl: undefined,
  };

  componentDidMount() {
    this.props.loadSeals();
    sealingService.signatures.fetch(this.props.userEmail).then((signatures) => {
      const randomIndex = Math.floor(Math.random() * signatures.length);
      const signatureUrl = signatures[randomIndex].image;
      this.setState({ signatureUrl });
    });
  }

  onSelectAnnotationTemplate = (selectedTemplate: number) => {
    this.setState({ selectedTemplate });
  };

  onSelectedPagesChange = (selectedPages: readonly number[]) => {
    this.setState({ selectedPages });
  };

  onToggleFirstPageOfEveryComponent = () => {
    this.setState((prevState) => ({
      selectedPages: toggleManyTogether(prevState.selectedPages, this.documents.getFirstPageOfEveryComponent()),
    }));
  };

  onToggleFirstPageOfEveryMetaPdf = () => {
    this.setState((prevState) => ({
      selectedPages: toggleManyTogether(prevState.selectedPages, this.documents.getFirstPageOfEveryMetaPdf()),
    }));
  };

  onToggleAll = () => {
    this.setState((prevState) => ({
      selectedPages: toggleManyTogether(prevState.selectedPages, this.documents.getEveryPage()),
    }));
  };

  onToggleCoverPage = () => {
    this.setState((prevState) => ({ createCoverPage: !prevState.createCoverPage }));
  };

  onToggleBackPage = () => {
    this.setState((prevState) => ({ createBackPage: !prevState.createBackPage }));
  };

  get documents() {
    return new DocumentHierarchy(this.props.components, this.props.metaPdfs);
  }

  handleSealJob = () => {
    this.props.confirmSealJob(
      this.state.selectedPages,
      this.state.selectedTemplate!,
      this.state.createCoverPage,
      this.state.createBackPage,
    );

    this.handleClose();
  };

  handleClose = () => {
    this.props.closeCurrentPopup();
  };

  renderMessageDialog(message: string) {
    return (
      <Dialog open onClose={this.handleClose}>
        <DialogTitle>Seal Job Special</DialogTitle>
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }

  render() {
    if (this.props.jobIsStamped) {
      return this.renderMessageDialog(
        'Sorry, this job has already been sealed. Please reset the job if you would like to seal again.',
      );
    }
    if (!this.props.hasSealsAndSignaturesPermission) {
      return this.renderMessageDialog('Sorry, you do not have the right permissions to seal a job.');
    }
    if (this.props.unqualifiedStates.length > 0) {
      const unqualifiedStates = this.props.unqualifiedStates.join(', ');
      return this.renderMessageDialog(`Sorry, you are not qualified to seal this job in ${unqualifiedStates}.`);
    }

    const toggleComponentFirstPageText = hasAll(this.state.selectedPages, this.documents.getFirstPageOfEveryComponent())
      ? 'Deselect first page of every component'
      : 'Select first page of every component';

    const toggleMetaPdfFirstPageText = hasAll(this.state.selectedPages, this.documents.getFirstPageOfEveryMetaPdf())
      ? 'Deselect first page of every "other" PDF'
      : 'Select first page of every "other" PDF';

    const toggleAllText = hasAll(this.state.selectedPages, this.documents.getEveryPage())
      ? 'Deselect all'
      : 'Select all';

    return (
      <Dialog open fullScreen onClose={this.handleClose} slots={{ transition: Transition }}>
        <AppBar
          sx={{
            position: 'relative',
            bgcolor: '#505050',
            borderBottom: 'solid 3px #f4d166',
            color: '#fff',
          }}
        >
          <Toolbar disableGutters>
            <Typography variant="h6" sx={{ color: 'inherit', width: '100%', maxWidth: 360, mr: 'auto', ml: 3 }}>
              Seal Job Special
            </Typography>
            <Button sx={{ m: 1, mr: 2 }} variant="contained" onClick={this.handleSealJob} aria-label="Seal Job">
              Seal Job
            </Button>
            <IconButton
              color="inherit"
              onClick={this.handleClose}
              aria-label="Close"
              sx={{ height: 64, width: 64, fontSize: 28, bgcolor: 'paragon.main', borderRadius: 0 }}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ display: 'flex', p: 3, flexGrow: 1, bgcolor: 'drawerDialog.main' }}>
          <Box sx={{ flex: 1, p: 3 }}>
            <MaterialDocumentTree
              components={this.props.components}
              metaPdfs={this.props.metaPdfs}
              selectedPages={this.state.selectedPages}
              onSelectedPagesChange={this.onSelectedPagesChange}
            />
            <Button size="small" variant="outlined" sx={buttonSx} onClick={this.onToggleFirstPageOfEveryComponent}>
              {toggleComponentFirstPageText}
            </Button>
            <Button size="small" variant="outlined" sx={buttonSx} onClick={this.onToggleFirstPageOfEveryMetaPdf}>
              {toggleMetaPdfFirstPageText}
            </Button>
            <Button size="small" variant="outlined" sx={buttonSx} onClick={this.onToggleAll}>
              {toggleAllText}
            </Button>
          </Box>
          <Box sx={{ flex: 1, p: 3 }}>
            <Box sx={{ ml: 1 }}>
              <Typography> Select a Template to Apply: </Typography>
              <TemplateDirectory
                templates={this.props.templates}
                selectedTemplate={this.state.selectedTemplate!}
                onSelectTemplate={this.onSelectAnnotationTemplate}
              />
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.state.createCoverPage}
                      onChange={this.onToggleCoverPage}
                      id="cover-page-checkbox"
                    />
                  }
                  label="Create Cover Page"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={this.state.createBackPage}
                      onChange={this.onToggleBackPage}
                      id="back-page-checkbox"
                    />
                  }
                  label="Create Back Page"
                />
              </FormGroup>
            </Box>
          </Box>
          <Box sx={{ flex: 1, p: 3 }}>
            <SealPreview sealUrl={this.props.sealUrl} signatureUrl={this.state.signatureUrl} />
          </Box>
        </Box>
      </Dialog>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const [metaPdfs, components] = getMetaPdfsAndComponentsAsSortedArrays(state);

  const user = state.users.userData[state.logInData.loggedInUser!.email];
  const userEmail = user.email;
  const jobRegion = state.job.regions[0];

  const sealUrl = state.seals.sealData[jobRegion] ? state.seals.sealData[jobRegion].image : undefined;

  // TODO: load the list of states the user isn't authorized to seal jobs for
  const unqualifiedStates: readonly string[] = [];

  return {
    userEmail,
    jobRegion,
    designOrigin: state.job.designOrigin,
    sealUrl,

    unqualifiedStates,
    hasSealsAndSignaturesPermission: hasSealsAndSignaturesPermission(state),

    metaPdfs,
    components,

    jobIsStamped: state.job.stampedByEmail != null,

    templates: getSortedAnnotationTemplates(state),
    selectedTemplate: state.annotationTemplates.annotationTemplates[state.annotationTemplates.selectedTemplate!],
  };
};

const actionCreators = {
  loadSeals,
  confirmSealJob,
  closeCurrentPopup,
};

export default connect(mapStateToProps, actionCreators)(SealJobSpecial);
